import { useState, Fragment } from 'react'
import { Printer, Check, Loader2 } from 'lucide-react'
import { PageSpinner } from '@/components/ui/Spinner'
import { cn } from '@/lib/utils'
import { useCateringPricing, useSaveCateringPricing } from '@/hooks/useCatering'
import { useMenuItems, useMenuCategories, groupMenuItemsByCategory } from '@/hooks/useMenu'
import type { CateringItemPricing, MenuItem } from '@/types/app'

// Every "size" a product can be catering-priced in. Tray sizes show a serves
// count; per-lb options show a minimum order (lbs) instead — same product,
// same row, just more columns. Shared by the on-screen grid and the printed
// price list below, so the two can't drift out of sync with each other.
const TRAY_GROUPS: {
  label: string
  price: keyof CateringItemPricing
  serves: keyof CateringItemPricing | null
  min: keyof CateringItemPricing | null
}[] = [
  { label: 'Half Tray',        price: 'half_tray_price',      serves: 'half_tray_serves',   min: null },
  { label: 'Medium Tray',      price: 'medium_tray_price',    serves: 'medium_tray_serves',  min: null },
  { label: 'Full Tray',        price: 'full_tray_price',      serves: 'full_tray_serves',    min: null },
  { label: 'Per Piece',        price: 'per_piece_price',      serves: null,                  min: null },
  { label: 'Per Lb (Loose)',   price: 'per_lb_price',         serves: null,                  min: 'per_lb_min' },
  { label: 'Per Lb (Boxed)',   price: 'boxed_per_lb_price',   serves: null,                  min: 'boxed_per_lb_min' },
]

function printPricingList(
  grouped: { cat: { id: string; name: string }; items: MenuItem[] }[],
  pricingMap: Map<string, CateringItemPricing> | undefined,
) {
  const BRAND = '#c94a1a'
  const logoUrl = `${window.location.origin}/logo-amruthahar.png`
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })

  function fmtP(v: number | null | undefined) { return v != null ? `$${v.toFixed(2)}` : '—' }
  function fmtSub(g: typeof TRAY_GROUPS[number], v: number | null | undefined) {
    if (v == null) return ''
    return g.min ? `min ${v} lb` : `serves ${v}`
  }

  const categoryBlocks = grouped.map(({ cat, items }) => {
    const rows = items.map(item => {
      const p = pricingMap?.get(item.id)
      const cols = TRAY_GROUPS.map(g => ({
        price: p?.[g.price] as number | null | undefined,
        sub:   g.serves ? (p?.[g.serves] as number | null | undefined) : g.min ? (p?.[g.min] as number | null | undefined) : null,
        g,
      }))
      const hasAny = cols.some(c => c.price != null)
      return `<tr style="border-bottom:1px solid #f0ece8">
        <td style="padding:7px 10px;font-weight:500;color:#1c1917;width:40%">${item.name}</td>
        ${hasAny ? cols.map(c => `
        <td style="padding:7px 10px;text-align:center;border-left:1px solid #f0ece8">
          <div style="font-weight:${c.price != null ? '600' : '400'};color:${c.price != null ? '#1c1917' : '#d1d5db'}">${fmtP(c.price)}</div>
          ${c.sub != null ? `<div style="font-size:10px;color:#9ca3af;margin-top:1px">${fmtSub(c.g, c.sub)}</div>` : ''}
        </td>`).join('') : `<td colspan="${TRAY_GROUPS.length}" style="padding:7px 10px;color:#d1d5db;font-style:italic;font-size:11px;border-left:1px solid #f0ece8">No pricing set</td>`}
      </tr>`
    }).join('')

    return `
    <div style="margin-bottom:10px;border:1px solid #e5ddd4;border-radius:8px;overflow:hidden;page-break-inside:avoid;break-inside:avoid">
      <div style="padding:6px 12px;background:#fff4ee;border-bottom:1px solid #f0ece8">
        <span style="font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:0.12em;color:${BRAND}">${cat.name}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;background:#fff">
        <thead>
          <tr style="background:#fafafa;border-bottom:1px solid #e5ddd4">
            <th style="padding:5px 10px;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;width:40%">ITEM</th>
            ${TRAY_GROUPS.map(g =>
              `<th style="padding:5px 10px;text-align:center;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#9ca3af;border-left:1px solid #f0ece8;width:${Math.floor(60 / TRAY_GROUPS.length)}%">${g.label.toUpperCase()}</th>`
            ).join('')}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`
  }).join('')

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Catering Price List</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1c1917;background:#f9f6f2;padding:20px}
  .wrap{max-width:700px;margin:0 auto}
  @page{size:A4 portrait;margin:12mm}
  @media print{
    html,body{padding:0;margin:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .wrap{max-width:100%}
  }
</style>
</head>
<body>
<div class="wrap">
  <div style="display:flex;align-items:center;gap:14px;padding-bottom:14px;margin-bottom:14px;border-bottom:2px solid ${BRAND}">
    <img src="${logoUrl}" style="width:48px;height:auto;object-fit:contain" alt="">
    <div>
      <div style="font-size:20px;font-weight:800;letter-spacing:-0.02em">Catering Price List</div>
      <div style="font-size:11px;color:#9ca3af">${today}</div>
    </div>
  </div>
  ${categoryBlocks}
</div>
<script>window.onload = () => window.print()</script>
</body>
</html>`

  const w = window.open('', '_blank')
  if (!w) return
  w.document.write(html)
  w.document.close()
}

// One table, always editable — no separate "view" vs "edit" layout to switch
// between. Each field saves on blur; the row briefly flashes green to confirm.
export function CateringPricingPage() {
  const { data: allItems = [], isLoading } = useMenuItems()
  const { data: categories = [] }          = useMenuCategories()
  const { data: pricingMap }               = useCateringPricing()
  const { mutateAsync: savePricing }       = useSaveCateringPricing()

  const [edits, setEdits]       = useState<Record<string, Partial<CateringItemPricing>>>({})
  const [savingId, setSavingId] = useState<string | null>(null)
  const [savedId, setSavedId]   = useState<string | null>(null)
  const [search, setSearch]     = useState('')
  // Which single tray-group cell is showing its inputs right now — clean text
  // display everywhere else, matching how the old read-only view looked.
  const [editingCell, setEditingCell] = useState<string | null>(null)

  const cateringItems = allItems
    .filter(i => i.available_catering)
    .filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()))

  const grouped = groupMenuItemsByCategory(cateringItems, categories)

  function getVal(menuItemId: string, field: keyof CateringItemPricing): string {
    const edited = edits[menuItemId]?.[field]
    if (edited !== undefined) return edited == null ? '' : String(edited)
    const stored = pricingMap?.get(menuItemId)?.[field]
    return stored == null ? '' : String(stored)
  }

  function setVal(menuItemId: string, field: keyof CateringItemPricing, value: string, isInt = false) {
    const num = value === '' ? null : isInt ? parseInt(value) : parseFloat(value)
    setEdits(p => ({ ...p, [menuItemId]: { ...(p[menuItemId] ?? {}), [field]: num } }))
  }

  // Fires on blur of any field in a row — saves the whole row's pending edits
  // at once so multiple changed fields in one row don't fire separate writes.
  async function commitRow(menuItemId: string) {
    if (!edits[menuItemId]) return
    setSavingId(menuItemId)
    const base  = pricingMap?.get(menuItemId) ?? {}
    const patch = edits[menuItemId]
    await savePricing({ ...base, ...patch, menu_item_id: menuItemId })
    setEdits(p => { const n = { ...p }; delete n[menuItemId]; return n })
    setSavingId(null)
    setSavedId(menuItemId)
    setTimeout(() => setSavedId(id => id === menuItemId ? null : id), 1200)
  }

  if (isLoading) return <PageSpinner />

  if (cateringItems.length === 0 && !search) {
    return (
      <div className="p-6 space-y-5">
        <div className="page-header">
          <h1 className="text-xl font-semibold text-stone-900">Catering Pricing</h1>
        </div>
        <div className="card p-12 text-center text-stone-400 text-sm">
          No items marked as available for catering.
          Go to <strong>Products</strong> → edit a product → enable <em>Catering</em> availability.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-5">
      <div className="page-header">
        <h1 className="text-xl font-semibold text-stone-900">Catering Pricing</h1>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <input
            type="search"
            placeholder="Search items…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 w-52"
          />
          <div className="flex items-center gap-2">
            <button
              onClick={() => printPricingList(grouped, pricingMap)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-300 text-xs font-medium text-stone-600 hover:bg-stone-50 transition-colors"
            >
              <Printer size={13} />
              Print Price List
            </button>
            <span className="text-2xs text-stone-400">Click any price to edit — saves automatically</span>
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-40" />
                {TRAY_GROUPS.map(g => <col key={g.label} className={g.serves || g.min ? 'w-28' : 'w-20'} />)}
                <col className="w-8" />
              </colgroup>
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-stone-500 uppercase tracking-wide">Product</th>
                  {TRAY_GROUPS.map(g => (
                    <th key={g.label} className="px-3 py-2.5 text-center text-xs font-semibold text-stone-500 uppercase tracking-wide border-l border-stone-100">
                      {g.label}
                    </th>
                  ))}
                  <th className="px-1 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {grouped.length === 0 && (
                  <tr>
                    <td colSpan={TRAY_GROUPS.length + 2} className="px-4 py-8 text-center text-sm text-stone-400">
                      No items match "{search}"
                    </td>
                  </tr>
                )}
                {grouped.map(({ cat, items }) => (
                  <Fragment key={cat.id}>
                    <tr className="bg-stone-50 border-y border-stone-200">
                      <td colSpan={TRAY_GROUPS.length + 2} className="px-4 py-1.5">
                        <span className="text-2xs font-bold uppercase tracking-widest text-stone-400">{cat.name}</span>
                      </td>
                    </tr>
                    {items.map(item => {
                      const isDirty  = !!edits[item.id]
                      const isSaving = savingId === item.id
                      const isSaved  = savedId === item.id
                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            'border-b border-stone-100 transition-colors',
                            isDirty ? 'bg-brand-50' : isSaved ? 'bg-success-light' : 'hover:bg-stone-50'
                          )}
                        >
                          <td className="px-4 py-2 font-medium text-stone-900 truncate">{item.name}</td>
                          {TRAY_GROUPS.map(g => {
                            const cellKey = `${item.id}:${g.label}`
                            const isEditing = editingCell === cellKey
                            const priceVal  = getVal(item.id, g.price)
                            const servesVal = g.serves ? getVal(item.id, g.serves) : ''
                            const minVal    = g.min ? getVal(item.id, g.min) : ''

                            if (!isEditing) {
                              const hasPrice = priceVal !== ''
                              return (
                                <td
                                  key={g.label}
                                  className="px-2 py-1.5 border-l border-stone-100 text-center cursor-text hover:bg-brand-50/50 transition-colors"
                                  onClick={() => setEditingCell(cellKey)}
                                  title="Click to edit"
                                >
                                  <p className={cn('font-semibold', hasPrice ? 'text-stone-900' : 'text-stone-300')}>
                                    {hasPrice ? `$${parseFloat(priceVal).toFixed(2)}${g.min ? '/lb' : ''}` : '—'}
                                  </p>
                                  {servesVal !== '' && <p className="text-2xs text-stone-400">serves {servesVal}</p>}
                                  {minVal !== '' && <p className="text-2xs text-stone-400">min {minVal} lb</p>}
                                </td>
                              )
                            }

                            return (
                              <td key={g.label} className="px-2 py-1.5 border-l border-stone-100">
                                <div
                                  className="flex items-center justify-center gap-1"
                                  onBlur={e => {
                                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                      commitRow(item.id)
                                      setEditingCell(null)
                                    }
                                  }}
                                >
                                  <span className="text-2xs text-stone-400">$</span>
                                  <input
                                    autoFocus
                                    type="text"
                                    inputMode="decimal"
                                    value={priceVal}
                                    onChange={e => setVal(item.id, g.price, e.target.value)}
                                    onFocus={e => e.target.select()}
                                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                                    placeholder="0.00"
                                    className="w-14 text-xs text-center border border-stone-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white"
                                  />
                                  {g.serves && (
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={servesVal}
                                      onChange={e => setVal(item.id, g.serves as keyof CateringItemPricing, e.target.value, true)}
                                      onFocus={e => e.target.select()}
                                      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                                      placeholder="srv"
                                      title="Serves (people)"
                                      className="w-11 text-2xs text-center border border-stone-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white text-stone-500"
                                    />
                                  )}
                                  {g.min && (
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={minVal}
                                      onChange={e => setVal(item.id, g.min as keyof CateringItemPricing, e.target.value)}
                                      onFocus={e => e.target.select()}
                                      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                                      placeholder="min lb"
                                      title="Minimum order (lbs)"
                                      className="w-12 text-2xs text-center border border-stone-300 rounded px-1 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 bg-white text-stone-500"
                                    />
                                  )}
                                </div>
                              </td>
                            )
                          })}
                          <td className="px-1 py-1.5 text-center">
                            {isSaving && <Loader2 size={12} className="animate-spin text-stone-400 mx-auto" />}
                            {isSaved && !isSaving && <Check size={12} className="text-success-dark mx-auto" />}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
